import React from 'react';
import { AlertCircle, ChevronRight, RefreshCw } from 'lucide-react';
import { FormattedChatMessage } from '../../../hooks/queries/chatTypes';
import { useUser } from '../../../hooks/queries/useAuth';
import { getUserDisplayName } from '../../../lib/user-utils';
import { TypingIndicator } from './TypingIndicator';

interface ChatMessagePairProps {
  userMessage: FormattedChatMessage;
  assistantMessage?: FormattedChatMessage;
  onMessageSelect: (message: FormattedChatMessage) => void;
  selectedMessageId?: string;
  isTyping?: boolean;
}

export function ChatMessagePair({
  userMessage,
  assistantMessage,
  onMessageSelect,
  selectedMessageId,
  isTyping = false,
}: ChatMessagePairProps) {
  const { data: user } = useUser();

  // Get user info
  const isCurrentUser = user?.id === userMessage.userId;
  const userDisplayName = isCurrentUser
    ? getUserDisplayName(user)
    : (userMessage.userMetadata?.displayName ?? 'User');

  // Determine if there was an error in the AI response
  const hasErrorResponse =
    assistantMessage &&
    (assistantMessage.content.includes('I apologize, but I encountered an error') ||
      assistantMessage.metadata?.isError);

  // Get timestamp from the user message
  // Use the createdAt field from the database
  const date = new Date(userMessage.createdAt);

  // Format the answer preview
  const formatAnswer = () => {
    if (!assistantMessage) return 'Waiting for response...';

    if (hasErrorResponse) return '❌ Error in response';

    if (assistantMessage.summary) return assistantMessage.summary;

    const firstLine = assistantMessage.content.split('\n')[0].trim();
    return firstLine.length < 100 ? firstLine : firstLine.substring(0, 97) + '...';
  };

  return (
    <div
      className={`group w-full ${hasErrorResponse ? '' : 'cursor-pointer'} relative mb-4`}
      onClick={() => {
        // Only open detail panel for non-error messages
        if (!hasErrorResponse) {
          onMessageSelect(assistantMessage || userMessage);
        }
      }}
    >
      {/* Simplified chat message card - matches note card design */}
      <div className="w-full">
        <div
          className={`rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-all ${
            selectedMessageId === userMessage.id || selectedMessageId === assistantMessage?.id
              ? 'ring-1 ring-blue-200'
              : ''
          }`}
        >
          {/* Header with user and date info - light gray background */}
          <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center bg-gray-50">
            <div className="flex items-center">
              <span className="font-medium text-sm text-gray-800">{userDisplayName}</span>
              <span className="mx-2 text-gray-300">•</span>
              <span className="text-xs text-gray-500">
                {date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </span>
            </div>
          </div>

          {/* Message content */}
          <div className="p-4">
            {/* Question preview */}
            <div className="text-sm text-gray-700 break-words line-clamp-2 mb-3">
              {userMessage.content}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100 my-2"></div>

            {/* Answer section with label */}
            <div className="mt-2 relative">
              {!assistantMessage ? (
                isTyping ? (
                  <div className="flex items-center gap-2 py-1">
                    <span className="text-xs font-medium text-gray-500">AI is thinking</span>
                    <TypingIndicator />
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 italic py-1">Waiting for response...</div>
                )
              ) : hasErrorResponse ? (
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-medium text-red-600 mb-1">Error</p>
                  <div className="text-sm text-red-600 leading-relaxed flex items-center gap-1.5">
                    <AlertCircle size={14} />
                    <span>AI encountered an error</span>
                  </div>
                  <button
                    className="self-start mt-2 text-blue-600 hover:text-blue-700 text-xs font-medium flex items-center gap-1 transition-colors"
                    onClick={e => {
                      e.stopPropagation(); // Prevent opening detail view
                      // Create a special ID that signals this is a retry request
                      onMessageSelect({ ...assistantMessage, id: `retry:${assistantMessage.id}` });
                    }}
                  >
                    <RefreshCw size={12} />
                    <span>Retry</span>
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-xs font-medium text-green-600 mb-1">Answer Summary</p>
                  <div className="text-sm font-bold text-gray-800 leading-relaxed line-clamp-3">
                    {formatAnswer()}
                  </div>
                </>
              )}

              {/* Only show the chevron for non-error messages */}
              {!hasErrorResponse && assistantMessage && (
                <div className="absolute top-0 right-0 text-gray-400 group-hover:text-gray-600 transition-colors">
                  <ChevronRight
                    size={16}
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
